import { useRequest } from 'ahooks';
import useFilter from './useFilter';
import { getPlatformData } from './service';
import { message, Tooltip } from 'antd';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react';
import { LoadingOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { initEcharts } from 'lib/echarts';
import { StarRate } from 'views/Review/components/Profile';
import { formatNumber, NumeralFormatPattern } from 'utils/numberFormat';
import styled from '@emotion/styled';
import { getChartMaxValue } from '../Activity/useActivityContents';

const getHour = (num: number, unit: number) =>
  typeof num === 'number'
    ? unit === 5
      ? num
      : unit === 4
      ? num * 24
      : unit === 3
      ? Math.ceil(num / 60 / 24)
      : undefined
    : undefined;

function Shopee({
  visible,
  departments,
  onUpdateTimeChange,
}: {
  visible: boolean;
  departments: Dashboard.Department[];
  onUpdateTimeChange: Dispatch<SetStateAction<string | undefined>>;
}) {
  const {
    t,
    storeId,
    range: [start, end],
    filter,
  } = useFilter({
    platform: 'SHOPEE',
    visible,
    departments,
  });

  const { loading, data } = useRequest(
    () =>
      getPlatformData({
        storeId: storeId as any,
        startTime: start.format('YYYY-MM-DD HH:mm:ss'),
        endTime: end.format('YYYY-MM-DD HH:mm:ss'),
      }).then((res) => {
        if (res.length > 0) {
          onUpdateTimeChange((pre) => pre || res[0].dt);
        }
        return res
          .map(({ data, ...others }) => {
            return {
              ...others,
              data: (JSON.parse(data) as any[]).reduce<any>((res, cur) => {
                res[cur.metric_name] = [cur.current_period, cur.unit];
                return res;
              }, {}),
            };
          })
          .reverse();
      }),
    {
      ready: Boolean(storeId),
      refreshDeps: [storeId, start, end],
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const {
    rate,
    csatRate,
    responseRate,
    responseTime,
    averageChatResponseTime,
    nonFulfillmentRate,
    cancellationRate,
    returnRefundRate,
    lateShipmentRate,
  } = useMemo(() => {
    if (!data?.length) {
      return {};
    }
    const render = (
      fieldOrGetter: string | ((data: any) => any),
      formatter: NumeralFormatPattern = '0.00%',
      percent = 100,
    ) => {
      const list = data
        .map((cur) =>
          typeof fieldOrGetter === 'string'
            ? cur.data[fieldOrGetter]?.[0]
            : fieldOrGetter(cur.data),
        )
        .filter((_) => typeof _ === 'number');
      if (!list.length) {
        return undefined;
      }
      return formatNumber(
        list.reduce((res, cur) => res + cur, 0) / list.length / percent,
        formatter,
      );
    };
    return {
      rate: render('shop_rating', '0.00', 1),
      csatRate: render('csat_rate'),
      responseRate: render('response_rate'),
      responseTime: render(
        ({ response_time: [num, unit] = [] }) => getHour(num, unit),
        '0.0000',
        1,
      ),
      averageChatResponseTime: render(
        ({ average_chat_response_time: [num, unit] = [] }) => getHour(num, unit),
        '0.0000',
        1,
      ),
      nonFulfillmentRate: render('non_fulfillment_rate'),
      cancellationRate: render('cancellation_rate'),
      returnRefundRate: render('return_refund_rate'),
      lateShipmentRate: render('late_shipment_rate'),
    };
  }, [data]);

  const chartRef1 = useRef<HTMLDivElement>(null);
  const chartRef2 = useRef<HTMLDivElement>(null);
  const chartRef3 = useRef<HTMLDivElement>(null);
  const chartRef4 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      !data ||
      !chartRef1.current ||
      !chartRef2.current ||
      !chartRef3.current ||
      !chartRef4.current
    ) {
      return;
    }

    const chart1 = initEcharts(chartRef1.current);
    const chart2 = initEcharts(chartRef2.current);
    const chart3 = initEcharts(chartRef3.current);
    const chart4 = initEcharts(chartRef4.current);

    const percentFormatter = (value: any) =>
      typeof value === 'number' ? formatNumber(value / 100, '0.00%') : '-';

    chart1.setOption({
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        data: [t('lazadaRating.sellerRating')],
        top: 0,
        left: 'center',
      },
      grid: {
        left: '2%',
        right: '2%',
        top: '10%',
        bottom: 0,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: data.map(({ countDt }) => countDt),
        axisLabel: {
          formatter: (value: string) => value.slice(5),
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 5,
      },
      series: [
        {
          name: t('lazadaRating.sellerRating'),
          type: 'line',
          data: data.map(({ data }) => data.shop_rating?.[0]),
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#5B8FF9',
          },
          tooltip: {
            valueFormatter: (value: any) =>
              typeof value === 'number' ? formatNumber(value, '0.00') : '-',
          },
        },
      ],
    });

    const csatRateList = data.map(({ data }) => data.csat_rate?.[0]);
    const noCsatRateList = csatRateList.map((_) => (typeof _ === 'number' ? 100 - _ : undefined));

    chart2.setOption({
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        selectedMode: false,
        data: [t('好评'), t('一般&差评')],
        top: 0,
        left: 'center',
      },
      grid: {
        left: '2%',
        right: '2%',
        top: '10%',
        bottom: '2%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: data.map(({ countDt }) => countDt),
        axisLabel: {
          formatter: (value: string) => value.slice(5),
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        interval: 20,
        show: false,
      },
      series: [
        {
          name: t('好评'),
          type: 'bar',
          stack: 'total',
          barMaxWidth: '20px',
          data: csatRateList,
          itemStyle: {
            color: '#5B8FF9',
          },
          tooltip: {
            valueFormatter: percentFormatter,
          },
        },
        {
          name: t('一般&差评'),
          type: 'bar',
          stack: 'total',
          barMaxWidth: '20px',
          data: noCsatRateList,
          itemStyle: {
            color: '#e3e3e3',
          },
          tooltip: {
            valueFormatter: percentFormatter,
          },
        },
      ],
    });

    const responseTimeList = data.map(({ data: { response_time: [num, unit] = [] } }) =>
      getHour(num, unit),
    );
    const averageChatResponseTimeList = data.map(
      ({ data: { average_chat_response_time: [num, unit] = [] } }) => getHour(num, unit),
    );
    const max3 = getChartMaxValue(
      [...responseTimeList, ...averageChatResponseTimeList].filter((_) => typeof _ === 'number'),
      5,
      true,
    );

    const hourRender = (value: any) =>
      typeof value === 'number' ? `${formatNumber(value, '0.0000')}${t('marketing.小时')}` : '-';

    chart3.setOption({
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        data: [t('回复率'), t('首次响应时长'), t('平均响应时长')],
        top: 0,
        left: 'center',
      },
      grid: {
        left: '2%',
        right: '3%',
        top: '10%',
        bottom: '2%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: data.map(({ countDt }) => countDt),
        axisLabel: {
          formatter: (value: string) => value.slice(5),
        },
      },
      yAxis: [
        {
          type: 'value',
          min: 0,
          max: 100,
          interval: 20,
        },
        {
          type: 'value',
          min: 0,
          max: max3,
          splitNumber: 5,
          interval: max3 / 5,
        },
      ],
      series: [
        {
          name: t('回复率'),
          type: 'line',
          data: data.map(({ data }) => data.response_rate?.[0]),
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#F6BD16',
          },
          tooltip: {
            valueFormatter: percentFormatter,
          },
        },
        {
          name: t('首次响应时长'),
          type: 'bar',
          yAxisIndex: 1,
          barMaxWidth: '40px',
          data: responseTimeList,
          itemStyle: {
            color: '#5B8FF9',
          },
          tooltip: {
            valueFormatter: hourRender,
          },
        },
        {
          name: t('平均响应时长'),
          type: 'bar',
          yAxisIndex: 1,
          barMaxWidth: '40px',
          data: averageChatResponseTimeList,
          itemStyle: {
            color: '#5AD8A6',
          },
          tooltip: {
            valueFormatter: hourRender,
          },
        },
      ],
    });

    const nonFulfillmentRateList = data.map(({ data }) => data.non_fulfillment_rate?.[0]);
    const cancellationRateList = data.map(({ data }) => data.cancellation_rate?.[0]);
    const returnRefundRateList = data.map(({ data }) => data.return_refund_rate?.[0]);
    const lateShipmentRateList = data.map(({ data }) => data.late_shipment_rate?.[0]);
    const max4 = getChartMaxValue(
      [
        ...nonFulfillmentRateList,
        ...cancellationRateList,
        ...returnRefundRateList,
        ...lateShipmentRateList,
      ].filter((_) => typeof _ === 'number'),
      5,
    );

    chart4.setOption({
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        data: [t('未完成率'), t('取消率'), t('退换货率'), t('延迟发货率')],
        top: 0,
        left: 'center',
      },
      grid: {
        left: '2%',
        right: '2%',
        top: '10%',
        bottom: '2%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: data.map(({ countDt }) => countDt),
        axisLabel: {
          formatter: (value: string) => value.slice(5),
        },
      },
      yAxis: [
        {
          type: 'value',
          min: 0,
          max: max4,
          interval: max4 / 5,
        },
      ],
      series: [
        {
          name: t('未完成率'),
          type: 'line',
          data: nonFulfillmentRateList,
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#5B8FF9',
          },
          tooltip: {
            valueFormatter: percentFormatter,
          },
        },
        {
          name: t('取消率'),
          type: 'line',
          data: cancellationRateList,
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#5AD8A6',
          },
          tooltip: {
            valueFormatter: percentFormatter,
          },
        },
        {
          name: t('退换货率'),
          type: 'line',
          data: returnRefundRateList,
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#5D7092',
          },
          tooltip: {
            valueFormatter: percentFormatter,
          },
        },
        {
          name: t('延迟发货率'),
          type: 'line',
          data: lateShipmentRateList,
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#F6BD16',
          },
          tooltip: {
            valueFormatter: percentFormatter,
          },
        },
      ],
    });

    const handleResize = () => {
      chart1.resize();
      chart2.resize();
      chart3.resize();
      chart4.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart1.dispose();
      chart2.dispose();
      chart3.dispose();
      chart4.dispose();
    };
  }, [data, t]);

  if (!data || !storeId) {
    return (
      <>
        {filter}
        {loading ? <LoadingOutlined /> : <></>}
      </>
    );
  }

  const style = visible ? undefined : { display: 'none' };

  return (
    <>
      {filter}
      <section style={style}>
        <header>
          <h3>{t('lazadaRating.sellerRating')}</h3>
          <Tooltip title={t('店铺评价tips')}>
            <QuestionCircleOutlined />
          </Tooltip>
        </header>
        <div>
          <ul>
            <dl>
              <dt>{t('lazadaRating.sellerRating')}</dt>
              {rate ? (
                <dd>
                  <StarRate disabled value={Number(rate)} />
                  {rate}
                </dd>
              ) : (
                <dd>-</dd>
              )}
            </dl>
          </ul>
          <section ref={chartRef1} />
        </div>
      </section>
      <section style={style}>
        <header>
          <h3>{t('聊天满意度')}</h3>
          <Tooltip title={t('聊天满意度tips')}>
            <QuestionCircleOutlined />
          </Tooltip>
        </header>
        <div>
          <ul>
            <dl>
              <dt>{t('聊天满意度')}</dt>
              <dd>{csatRate || '-'}</dd>
            </dl>
          </ul>
          <section ref={chartRef2} />
        </div>
      </section>
      <section style={style}>
        <header>
          <h3>{t('客户服务')}</h3>
        </header>
        <div>
          <ul>
            <dl>
              <dt>
                {t('回复率')}
                <Tooltip title={t('回复率tips2')}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </dt>
              <dd>{responseRate || '-'}</dd>
            </dl>
            <dl>
              <dt>
                {t('首次响应时长')}
                <Tooltip title={t('首次响应时长tips')}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </dt>
              <dd>{responseTime ? `${responseTime} ${t('marketing.小时')}` : '-'}</dd>
            </dl>
            <dl>
              <dt>
                {t('平均响应时长')}
                <Tooltip title={t('平均响应时长tips3')}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </dt>
              <dd>
                {averageChatResponseTime
                  ? `${averageChatResponseTime} ${t('marketing.小时')}`
                  : '-'}
              </dd>
            </dl>
          </ul>
          <section ref={chartRef3} />
        </div>
      </section>
      <section style={style}>
        <header>
          <h3>{t('订单表现')}</h3>
        </header>
        <div>
          <ul>
            <DL1>
              <dt>
                {t('未完成率')}
                <Tooltip title={t('未完成率tips')}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </dt>
              <dd>{nonFulfillmentRate || '-'}</dd>
            </DL1>
            <DL2>
              <dt>
                {t('取消率')}
                <Tooltip title={t('取消率tips')}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </dt>
              <dd>{cancellationRate || '-'}</dd>
            </DL2>
            <dl>
              <dt>
                {t('延迟发货率')}
                <Tooltip title={t('延迟发货率tips')}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </dt>
              <dd>{lateShipmentRate || '-'}</dd>
            </dl>
          </ul>
          <ul>
            <dl style={{ opacity: 0 }} />
            <DL3>
              <dt>
                {t('退换货率')}
                <Tooltip title={t('退换货率tips')}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </dt>
              <dd>{returnRefundRate || '-'}</dd>
            </DL3>
          </ul>
          <section ref={chartRef4} />
        </div>
      </section>
    </>
  );
}

const DL = styled.dl`
  position: relative;
  &::after {
    content: '';
    display: inline-block;
    position: absolute;
    background: #e8e8e8;
  }
`;

const DL1 = styled(DL)`
  &::after {
    top: 22px;
    right: -20px;
    width: 20px;
    height: 1px;
  }
`;

const DL2 = styled(DL)`
  &::after {
    top: 22px;
    left: -10px;
    width: 1px;
    height: 60px;
  }
`;

const DL3 = styled(DL)`
  &::after {
    top: 22px;
    left: -10px;
    width: 10px;
    height: 1px;
  }
`;

export default Shopee;
